import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  Modal,
  Form,
  Row,
  Col,
  message,
  DatePicker,
  Select,
  TimePicker,
  Input,
} from "antd";
import moment, { Moment } from "moment";
import { handleCreateSlots } from "../../../../apis/slots";
import { handleFetchSites } from "../../../../apis/sites";
import { useState, useEffect } from "react";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from "@hello-pangea/dnd";
import { Grip, XCircle } from "lucide-react";

const { Option } = Select;

interface Unit {
  id: string;
  startTime: string;
  endTime: string;
  limit: number;
}

const generateUniqueId = (): string => {
  return `slot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export default function CreateSlots({ isVisible, setVisible }: any) {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const [units, setUnits] = useState<Unit[]>([
    { id: generateUniqueId(), startTime: "08:00", endTime: "09:00", limit: 5 },
    { id: generateUniqueId(), startTime: "09:00", endTime: "10:00", limit: 5 },
    { id: generateUniqueId(), startTime: "10:00", endTime: "11:00", limit: 5 },
    { id: generateUniqueId(), startTime: "11:00", endTime: "12:00", limit: 5 },
    { id: generateUniqueId(), startTime: "12:00", endTime: "13:00", limit: 5 },
    { id: generateUniqueId(), startTime: "13:00", endTime: "14:00", limit: 5 },
    { id: generateUniqueId(), startTime: "14:00", endTime: "15:00", limit: 5 },
    { id: generateUniqueId(), startTime: "15:00", endTime: "16:00", limit: 5 },
  ]);

  useEffect(() => {
    console.log("Current units:", units);
  }, [units]);

  const handleStartTimeChange = (index: number, time: Moment | null, timeString: string) => {
    const newUnits = [...units];
    newUnits[index] = { ...newUnits[index], startTime: timeString };
    setUnits(newUnits);
  };

  const handleEndTimeChange = (index: number, time: Moment | null, timeString: string) => {
    const newUnits = [...units];
    newUnits[index] = { ...newUnits[index], endTime: timeString };
    setUnits(newUnits);
  };

  const handleLimitChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const newUnits = [...units];
    newUnits[index] = { ...newUnits[index], limit: parseInt(e.target.value) || 0 };
    setUnits(newUnits);
  };

  const handleAddLineItem = () => {
    setUnits([
      ...units,
      {
        id: generateUniqueId(),
        startTime: "00:00",
        endTime: "01:00",
        limit: 1,
      },
    ]);
  };

  const handleRemoveLineItem = (index: number) => {
    const newUnits = units.filter((_, i) => i !== index);
    setUnits(newUnits);
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    if (result.destination.index === result.source.index) return;

    const items = Array.from(units);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setUnits(items);
  };

  const { data: sites } = useQuery({
    queryKey: ["sites"],
    queryFn: handleFetchSites,
  });

  const { mutate, isPending } = useMutation({
    mutationFn: handleCreateSlots,
    onSuccess: () => {
      message.success("Created Successfully");
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ["slots"] });
      setVisible(false);
    },
    onError: (error) => {
      message.error(`${error}`);
    },
  });

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      const { date, site } = values;

      const utcDate = date.clone().startOf("day");
      const isoString = utcDate.format();

      const formattedUnits = units.map((item) => {
        const start = moment(`${utcDate.format("YYYY-MM-DD")} ${item.startTime}`, "YYYY-MM-DD HH:mm");
        const end = moment(`${utcDate.format("YYYY-MM-DD")} ${item.endTime}`, "YYYY-MM-DD HH:mm");

        return {
          startTime: start,
          endTime: end,
          limit: Number(item.limit),
        };
      });

      const data = { date: isoString, site, units: formattedUnits };
      await mutate(data);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const siteOptions = sites?.records?.map((site: any) => (
    <Option key={site?._id} value={site?._id}>
      {site?.name}
    </Option>
  ));

  const clearForm = () => {
    form.resetFields();
    setUnits([
      { id: generateUniqueId(), startTime: "08:00", endTime: "09:00", limit: 5 },
      { id: generateUniqueId(), startTime: "09:00", endTime: "10:00", limit: 5 },
      { id: generateUniqueId(), startTime: "10:00", endTime: "11:00", limit: 5 },
      { id: generateUniqueId(), startTime: "11:00", endTime: "12:00", limit: 5 },
      { id: generateUniqueId(), startTime: "12:00", endTime: "13:00", limit: 5 },
      { id: generateUniqueId(), startTime: "13:00", endTime: "14:00", limit: 5 },
      { id: generateUniqueId(), startTime: "14:00", endTime: "15:00", limit: 5 },
      { id: generateUniqueId(), startTime: "15:00", endTime: "16:00", limit: 5 },
    ]);
  };

  const getMomentValue = (timeString: string | undefined): Moment | undefined => {
    if (!timeString) return undefined;
    const momentValue = moment(timeString, "HH:mm");
    return momentValue.isValid() ? momentValue : undefined;
  };

  return (
    <Modal
      open={isVisible}
      title="Create Slots"
      onCancel={() => setVisible(false)}
      footer={[
        <Button key="back" onClick={clearForm}>
          Clear
        </Button>,
        <Button key="submit" type="primary" onClick={onSubmit} loading={isPending}>
          Create
        </Button>,
      ]}
      width={700}
      maskClosable={false}
      getContainer={false}
    >
      <Form form={form} layout="vertical">
        <Row gutter={[16, 0]}>
          <Col xs={24} lg={12}>
            <Form.Item name="site" label="Site" rules={[{ required: true, message: "Please select site" }]}>
              <Select placeholder="Select Site" allowClear>
                {siteOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item name="date" label="Date" rules={[{ required: true, message: "Please enter date" }]}>
              <DatePicker placeholder="Date" format="DD/MM/YYYY" style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <div style={{ marginTop: 24 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="slots-list">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{
                  backgroundColor: snapshot.isDraggingOver ? "#f0f9ff" : "transparent",
                  border: "1px solid #e8e8e8",
                  borderRadius: "8px",
                  minHeight: "200px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "50px 60px 1fr 1fr 100px 80px",
                    padding: "12px 16px",
                    backgroundColor: "#fafafa",
                    borderBottom: "1px solid #e8e8e8",
                    fontWeight: 600,
                    color: "#333",
                  }}
                  className="gap-x-2"
                >
                  <div></div>
                  <div>S/N</div>
                  <div>Start Time</div>
                  <div>End Time</div>
                  <div>Capacity</div>
                  <div>Action</div>
                </div>

                {units.map((unit, index) => (
                  <Draggable key={unit.id} draggableId={unit.id} index={index}>
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "50px 60px 1fr 1fr 100px 80px",
                          padding: "10px 16px",
                          backgroundColor: snapshot.isDragging ? "#e6f7ff" : "white",
                          borderBottom: index < units.length - 1 ? "1px solid #f0f0f0" : "none",
                          alignItems: "center",
                          boxShadow: snapshot.isDragging ? "0 4px 16px rgba(0,0,0,0.12)" : "none",
                          borderRadius: snapshot.isDragging ? "6px" : "0",
                          transition: "all 0.15s ease",
                          ...provided.draggableProps.style,
                        }}
                        className="gap-x-2"
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "#bfbfbf",
                            fontSize: "18px",
                            cursor: "grab",
                          }}
                        >
                          <Grip size="1em" />
                        </div>

                        <div style={{ fontWeight: 500, color: "#555" }}>{index + 1}</div>

                        <div>
                          <TimePicker
                            format="HH:mm"
                            value={getMomentValue(unit.startTime) as any}
                            onChange={(time: any, timeString: any) => handleStartTimeChange(index, time, timeString as string)}
                            style={{ width: "100%" }}
                            placeholder="Start"
                          />
                        </div>

                        <div>
                          <TimePicker
                            format="HH:mm"
                            value={getMomentValue(unit.endTime) as any}
                            onChange={(time: any, timeString: any) => handleEndTimeChange(index, time, timeString as string)}
                            style={{ width: "100%" }}
                            placeholder="End"
                          />
                        </div>

                        <div>
                          <Input
                            type="number"
                            value={unit.limit}
                            onChange={(e) => handleLimitChange(index, e)}
                            min={1}
                            style={{ width: "100%" }}
                          />
                        </div>

                        <div>
                          <Button
                            type="text"
                            danger
                            onClick={() => handleRemoveLineItem(index)}
                            disabled={units.length <= 1}
                            size="small"
                          >
                            <XCircle size="1.25em" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}

                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button type="dashed" onClick={handleAddLineItem} block style={{ marginTop: 16 }}>
          + Add Slot
        </Button>
      </div>
    </Modal>
  );
}